/**
 * Invitation codes. Shape and normalisation live here because both sides of
 * the wire need the same answer: the worker to look one up, the SPA to accept
 * whatever a person pastes into the box.
 *
 * `circle-XXXX-XXXX` over an alphabet with no `0/O`, `1/I/L`: the code is read
 * off a screen and typed into a phone, so the pairs that get mistaken for each
 * other are simply not in it.
 */

/** 31 symbols over 8 positions: ~40 bits — plenty for a code that is looked up
 * in a table and rate limited. */
export const INVITE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

export const INVITE_PREFIX = 'circle';

export const INVITE_CODE_RE = /^circle-[2-9A-HJKMNP-Z]{4}-[2-9A-HJKMNP-Z]{4}$/;

/**
 * Canonical form of whatever was typed, pasted or clicked, or null when there
 * is no code in it.
 *
 * Accepts the bare code, the code in any case, a `/i/<code>` link (the whole
 * URL, with or without an origin), and the eight body characters on their own
 * — the prefix and the dashes are decoration, not information.
 */
export function normaliseInviteCode(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // A pasted link: take the last non-empty path segment, before any ? or #.
  const fromLink = /\/i\/([^/?#\s]+)/i.exec(trimmed);
  const raw = fromLink ? fromLink[1]! : trimmed;

  const body = raw
    .toUpperCase()
    .replace(/^CIRCLE[-\s]*/i, '')
    .replace(/[^0-9A-Z]/g, '');
  if (body.length !== 8) return null;

  const code = `${INVITE_PREFIX}-${body.slice(0, 4)}-${body.slice(4)}`;
  return INVITE_CODE_RE.test(code) ? code : null;
}

/** `/i/<code>` — the link an invitation travels as. */
export function inviteLink(code: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}/i/${code}`;
}
